import React, { useState } from 'react';
import { X, Loader2, Save } from 'lucide-react';
import { apiClient } from '../services/api';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface Props {
  categoryId?: number;
  folderId?: number;
  storyId?: number;
  initialTitle?: string;
  initialText?: string;
  onClose: () => void;
  onSaved: () => void;
}

export const AddTextModal: React.FC<Props> = ({
  categoryId,
  folderId,
  storyId,
  initialTitle = '',
  initialText = '',
  onClose,
  onSaved,
}) => {
  useBodyScrollLock();
  const [title, setTitle] = useState(initialTitle);
  const [text, setText] = useState(initialText);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!storyId;

  const handleSave = async () => {
    if (!text.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      if (isEditing && storyId) {
        const data = { title: title.trim() || undefined, story: text.trim() };
        if (folderId) {
          await apiClient.updateFolderStory(folderId, storyId, data);
        } else if (categoryId) {
          await apiClient.updateStory(categoryId, storyId, data);
        }
      } else {
        const payload = { story: text.trim(), words_used: [] as string[], title: title.trim() || undefined };
        if (folderId) {
          await apiClient.saveFolderStory(folderId, payload);
        } else if (categoryId) {
          await apiClient.saveStory(categoryId, payload);
        }
      }
      onSaved();
      onClose();
    } catch {
      setError('Could not save the text. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-6">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg flex flex-col max-h-[85dvh] sm:max-h-[75dvh]">
        <div className="flex items-center justify-between px-5 pt-5 pb-4 flex-shrink-0 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">{isEditing ? 'Edit text' : 'Add text'}</h2>
          <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 flex flex-col overflow-y-auto overscroll-contain px-5 py-4 gap-3">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (optional)"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/70 focus:border-indigo-300 bg-white text-sm"
            disabled={isSaving}
            autoFocus
          />

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste or write your text here…"
            rows={10}
            className="w-full flex-1 min-h-[200px] px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/70 focus:border-indigo-300 bg-white text-sm resize-none"
            disabled={isSaving}
          />

          {error && <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">{error}</div>}

          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !text.trim()}
            className="w-full px-4 py-3 text-[15px] font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
            {isSaving ? 'Saving…' : isEditing ? 'Save changes' : 'Save text'}
          </button>
        </div>
      </div>
    </div>
  );
};
